import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import CrassAd from '~/src/components/crassAd/CrassAd';

import {initAdUnit} from '~/src/utilities/libs/totm';
import initPolyglot from '~/src/utilities/libs/polyglot';
import {classNames} from '~/src/utilities/helpers/css';
import {withErrorBoundary} from '~/src/components/errorBoundary/ErrorBoundary';
import {SettingsContext} from '../context/SettingsContext';
import {AdFormat} from '~/src/utilities/constants/formats';

import css from './Ad.module.scss';

import i18nStrings from './Ad.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const Ad = React.memo(({adUnitName, format = '', colorScheme = '', isCrassAd = false, className = null}) => {
    if (!adUnitName) {
        return null;
    }

    const {siteArea} = useContext(SettingsContext);

    const [crassAd, setCrassAd] = useState(null);

    const loadCrassAd = useCallback(
        (event) => {
            if (event.origin === 'https://tpc.googlesyndication.com' && event.data?.message === 'crass_teaser' && event.data?.adUnit?.endsWith(adUnitName)) {
                setCrassAd(JSON.parse(event.data?.teaser));
            }
        },
        [adUnitName]
    );

    const adContainer = useRef();
    const targetContainerId = useMemo(() => adUnitName.replace(/[^a-z0-9-]/gi, '_'), [adUnitName]);

    process.browser &&
        useEffect(() => {
            initAdUnit({
                adUnitName,
                targetContainerId,
                callback: (params) => {
                    if (!adContainer.current) {
                        return;
                    }
                    if (!params.isEmpty) {
                        adContainer.current.classList.remove(css.isHidden);

                        if (format !== AdFormat.INSIDE_QUARTER_POS) {
                            adContainer.current.parentElement.style.margin = '';
                        }

                        if (format && css[format]) {
                            adContainer.current.classList.add(css[format]);
                        }
                    } else {
                        adContainer.current.classList.add(css.isHidden);

                        if (format !== AdFormat.INSIDE_QUARTER_POS) {
                            adContainer.current.parentElement.style.margin = '0';
                        }
                    }
                },
                adHeader: !isCrassAd ? `<p class="${css.adTitle}">${getTranslation('title')}</p>` : false
            });
        }, []);

    process.browser &&
        useEffect(() => {
            if (isCrassAd) {
                window.addEventListener('message', loadCrassAd);

                return () => window.removeEventListener('message', loadCrassAd);
            }
        }, [loadCrassAd]);

    const classes = [classNames(css, 'adContainer', siteArea)];

    if (className) {
        classes.push(className);
    }

    return (
        <div className={classes.join(' ')} id={targetContainerId} ref={adContainer} data-testid="Ad">
            {crassAd && <CrassAd {...crassAd} format={format} colorScheme={colorScheme} />}
        </div>
    );
});

if (process.env.NODE_ENV === 'development') {
    Ad.propTypes = {
        adUnitName: PropTypes.string.isRequired,
        className: PropTypes.string,
        format: PropTypes.string,
        colorScheme: PropTypes.string
    };
}

export default withErrorBoundary(Ad, 'Ad');
